import { supabase } from "../supabase";
import {
  commercialInputValue,
  readCommercialAmount,
} from "./commercialAmounts";
import { isOffline } from "./connectivity";
import { getActiveCacheOrganizationId, readOrgScopedJson, writeOrgScopedJson } from "./orgCacheStorage";
import {
  applyOrganizationScope,
  orgScopedUpdateByPkIn,
  resolveOrganizationIdForQueries,
} from "./orgQueryScope";

export { commercialInputValue };

export const MATERIALS_CACHE_KEY = "CP_MATERIALS";

/**
 * Field catalog — identification only. The shared offline cache must not store
 * unit_cost, sell_price, or inventory. Pricing screens pass explicit columns
 * and receive those fields from the network response, not from this cache.
 * Add "manufacturer" here when the database column exists.
 */
export const FIELD_CATALOG_FIELDS = [
  "material_id",
  "material_name",
  "sku",
  "material_type",
  "unit_of_measure",
  "description",
  "active",
];

const PRICING_COLUMNS = new Set(["unit_cost", "sell_price", "current_inventory"]);

function resolveCatalogColumns(columns) {
  if (Array.isArray(columns) && columns.length) return columns;
  return FIELD_CATALOG_FIELDS;
}

export function catalogSelectIncludesPricing(columns) {
  return resolveCatalogColumns(columns).some((column) => PRICING_COLUMNS.has(column));
}

function parseInventoryQuantity(value) {
  if (value == null || String(value).trim() === "") {
    return { value: 0 };
  }

  const parsed = Number(String(value).trim());
  if (!Number.isFinite(parsed) || parsed < 0) {
    return { error: "Enter a valid inventory quantity, or leave it blank." };
  }

  return { value: parsed };
}

/**
 * Parse a catalog unit cost for materials.unit_cost (NOT NULL DEFAULT 0).
 * Blank is 0, matching the production column. A typed 0 stays 0.
 * present is false only when the field was left blank.
 */
export function parseCatalogUnitCost(value) {
  if (value == null || String(value).trim() === "") {
    return { value: 0, present: false };
  }

  const parsed = readCommercialAmount(value);
  if (parsed == null || parsed < 0) {
    return { error: "Enter a valid unit cost, or leave it blank." };
  }

  return { value: parsed, present: true };
}

/**
 * Build the materials catalog write payload.
 * $10 saves as 10. $12.50 saves as 12.5. 0 stays 0. Blank saves as 0.
 * When pricing was not loaded onto the row, a still-blank cost is omitted
 * so a failed pricing read cannot overwrite the stored cost with 0.
 * material_type is omitted the same way when it was not loaded and is blank.
 */
export function buildMaterialCatalogWrite(form, options = {}) {
  const pricingKnown = options.pricingKnown !== false;
  const inventoryKnown = options.inventoryKnown !== false;
  const typeKnown = options.typeKnown !== false;
  const name = String(form?.material_name ?? "").trim();

  if (!name) {
    return { error: "Enter a material name." };
  }

  const unitCost = parseCatalogUnitCost(form?.unit_cost);
  if (unitCost.error) return unitCost;

  const inventory = parseInventoryQuantity(form?.current_inventory);
  if (inventory.error) return inventory;

  const materialType = String(form?.material_type ?? "").trim();
  const payload = {
    material_name: name,
    sku: form?.sku ?? "",
    description: form?.description ?? "",
    unit_of_measure: form?.unit_of_measure ?? "",
    active: form?.active !== false,
  };

  if (typeKnown || materialType) {
    payload.material_type = materialType || null;
  }

  if (pricingKnown || unitCost.present) {
    payload.unit_cost = unitCost.value;
  }

  if (inventoryKnown || String(form?.current_inventory ?? "").trim() !== "") {
    payload.current_inventory = inventory.value;
  }

  return { value: payload };
}

export function normalizeFieldCatalogMaterial(material) {
  if (!material) return null;

  const row = {};
  for (const key of FIELD_CATALOG_FIELDS) {
    if (material[key] !== undefined) {
      row[key] = material[key];
    }
  }
  return row;
}

export function normalizeFieldCatalogMaterials(materials) {
  return (materials || [])
    .map(normalizeFieldCatalogMaterial)
    .filter(Boolean);
}

export function getCachedMaterials(organizationId = null) {
  try {
    const orgId = organizationId ?? getActiveCacheOrganizationId();
    const parsed = readOrgScopedJson(MATERIALS_CACHE_KEY, orgId);
    if (!Array.isArray(parsed)) return null;
    return normalizeFieldCatalogMaterials(parsed);
  } catch {
    return null;
  }
}

export function saveCachedMaterials(materials, organizationId = null) {
  const orgId = organizationId ?? getActiveCacheOrganizationId();
  writeOrgScopedJson(
    MATERIALS_CACHE_KEY,
    orgId,
    normalizeFieldCatalogMaterials(materials),
  );
}

export async function fetchAndCacheMaterialsCatalog(
  client = supabase,
  { relation = "materials_search", actor = null, columns = null } = {},
) {
  const organizationId = await resolveOrganizationIdForQueries(actor, client);
  const selectColumns = resolveCatalogColumns(columns);
  const includePricing = catalogSelectIncludesPricing(selectColumns);

  let query = client
    .from(relation)
    .select(selectColumns.join(", "))
    .eq("active", true)
    .order("material_name");

  query = applyOrganizationScope(query, organizationId, {
    tableName: "materials",
  });

  const { data, error } = await query;

  if (error) {
    console.error("MATERIALS CATALOG FETCH ERROR:", error);
    return { materials: getCachedMaterials(organizationId) || [], error };
  }

  const raw = data || [];
  // Shared field cache stays pricing-free for employee search.
  saveCachedMaterials(raw, organizationId);

  if (includePricing) {
    return { materials: raw, error: null };
  }

  return { materials: normalizeFieldCatalogMaterials(raw), error: null };
}

/**
 * Cache-first catalog load for field workflows.
 * Returns cached materials immediately when available, then refreshes online.
 */
export async function loadMaterialsCatalog({
  onCached,
  relation = "materials_search",
  actor = null,
  columns = null,
} = {}) {
  const includePricing = catalogSelectIncludesPricing(columns);
  const organizationId = actor
    ? await resolveOrganizationIdForQueries(actor, supabase)
    : null;
  const cached = getCachedMaterials(organizationId);

  // Pricing screens must not paint the stripped field cache as a cost of $NaN.
  if (!includePricing && cached?.length) {
    onCached?.(cached);
  }

  if (isOffline()) {
    return { materials: cached || [], fromCache: true, error: null };
  }

  const { materials, error } = await fetchAndCacheMaterialsCatalog(supabase, {
    relation,
    actor,
    columns,
  });

  if (!materials.length && cached?.length) {
    return { materials: cached, fromCache: true, error };
  }

  return { materials, fromCache: false, error };
}

/**
 * Soft-archive materials by deactivating them (existing catalog removal workflow).
 * RG-03 — requires actor; filters by organization_id as well as material_id.
 */
export async function archiveMaterials(
  materialIds,
  client = supabase,
  actor = null,
) {
  if (!materialIds?.length) {
    return { error: null };
  }

  const { error } = await orgScopedUpdateByPkIn(
    client,
    "materials",
    { active: false },
    actor,
    { pkColumn: "material_id", pkValues: materialIds },
  );

  return { error };
}
